import { useEffect, useRef } from "react";
import { EditorView } from "@codemirror/view";
import { EditorState, type Extension } from "@codemirror/state";
import { hydraEditorExtensions, setEditorReadOnly, type YjsBinding } from "./extensions";
import type { EditorCallbacks } from "./live-preview";

/**
 * React wrapper owning an EditorView. The document itself is owned by the
 * data layer: pass a Yjs binding (note docs) or an initial string (demo).
 * This component never re-creates the view unless `viewKey` changes.
 */
export function Editor({
  viewKey,
  initialDoc = "",
  yjs,
  extra,
  callbacks,
  onViewReady,
  className,
  readOnly = false,
}: {
  /** Identity of the underlying document; changing it rebuilds the view. */
  viewKey: string;
  initialDoc?: string;
  yjs?: YjsBinding;
  extra?: Extension[];
  callbacks?: Partial<EditorCallbacks>;
  /** may return a cleanup fn, run before the view is destroyed */
  onViewReady?: (view: EditorView) => void | (() => void);
  className?: string;
  /** Reconfigured live (no view rebuild): viewer role, offline shared notes. */
  readOnly?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const latest = useRef({ initialDoc, yjs, extra, callbacks, onViewReady, readOnly });
  latest.current = { initialDoc, yjs, extra, callbacks, onViewReady, readOnly };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const { initialDoc, yjs, extra, callbacks, onViewReady, readOnly } = latest.current;
    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: yjs ? yjs.text.toString() : initialDoc,
        extensions: hydraEditorExtensions({ callbacks, extra, yjs, readOnly }),
      }),
    });
    viewRef.current = view;
    const cleanup = onViewReady?.(view);
    return () => {
      cleanup?.();
      viewRef.current = null;
      view.destroy();
    };
  }, [viewKey]);

  useEffect(() => {
    if (viewRef.current) setEditorReadOnly(viewRef.current, readOnly);
  }, [readOnly]);

  return <div ref={hostRef} className={className} style={{ height: "100%", minHeight: 0 }} />;
}
